import LoggingService from 'components/core/logging/LoggingService';
import { DatadogCustomAction } from 'enums/datadogCustomAction';
import { getReleaseIdentifier } from 'utils/deviceInfoUtils';
import { storageManager, StorageType } from 'utils/storageUtils';

export const STALE_CHUNK_RELOAD_ATTEMPTED_KEY = 'staleChunkReloadAttempted';

const reloadedReleaseStorage = storageManager.createOrFetchStorage<string | undefined>(
  STALE_CHUNK_RELOAD_ATTEMPTED_KEY,
  StorageType.SESSION
);

const CHUNK_LOAD_ERROR_NAME = 'ChunkLoadError';

/**
 * Native ESM wording, for chunks fetched by `import()` rather than Rspack's runtime. Each engine
 * words this differently and none of them set a distinguishing error name, so the message is the only
 * signal available.
 */
const NATIVE_IMPORT_FAILURES = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

export const RELOAD_STALL_TIMEOUT_MS = 10_000;

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error &&
  (error.name === CHUNK_LOAD_ERROR_NAME || NATIVE_IMPORT_FAILURES.some(message => error.message.includes(message)));

const tryPersistReloadedRelease = (releaseIdentifier: string): boolean => {
  try {
    reloadedReleaseStorage.set(releaseIdentifier);

    return true;
  } catch {
    return false;
  }
};

/**
 * Holds the Suspense fallback on screen while the reload takes effect, since settling straight away
 * would let React render the route or surface the error boundary mid-teardown. A reload that has not
 * happened by the timeout has stalled, so reject and let the error boundary offer its retry instead of
 * leaving the call sites that pass no page loader blank indefinitely.
 *
 * @param error - The original failure, rethrown if the reload never lands.
 */
const settleOnStalledReload = <T>(error: unknown): Promise<T> =>
  new Promise<T>((_resolve, reject) => {
    const stallTimer = setTimeout(() => reject(error), RELOAD_STALL_TIMEOUT_MS);

    /*
     * The old document keeps running timers until the replacement commits, so on a slow network the
     * stall timer can outlive a reload that was going to succeed. `pagehide` means the navigation is
     * happening after all: stay pending and let the page go, rather than flashing the error boundary.
     */
    globalThis.addEventListener('pagehide', () => clearTimeout(stallTimer), { once: true });
  });

/**
 * Runs a dynamic import, reloading the page at most once per build when the chunk it asks for no
 * longer exists.
 *
 * A tab left open across a release still references its own build's hashed chunks. Once a deploy has
 * removed them the CDN answers with index.html rather than a 404, so the browser receives HTML where
 * it expected JavaScript and the import rejects. Reloading pulls the current index.html and the chunks
 * that match it. Offline failures are left to the caller: a reload cannot fix them and would trade the
 * app for the browser's own error page.
 *
 * @param load - The dynamic import to run.
 * @param shouldReload - Reads current caller intent before attempting a stale-chunk reload.
 */
export const importWithStaleChunkRecovery = async <T>(
  load: () => Promise<T>,
  shouldReload: () => boolean = () => true
): Promise<T> => {
  try {
    return await load();
  } catch (error) {
    const releaseIdentifier = getReleaseIdentifier();
    if (
      !shouldReload() ||
      !isChunkLoadError(error) ||
      reloadedReleaseStorage.get() === releaseIdentifier ||
      !navigator.onLine
    ) {
      throw error;
    }

    if (!tryPersistReloadedRelease(releaseIdentifier)) {
      throw error;
    }

    LoggingService.trackAction(DatadogCustomAction.STALE_CHUNK_RELOAD, { error, releaseIdentifier });
    globalThis.location.reload();

    return settleOnStalledReload<T>(error);
  }
};
